import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {Alert, AlertTitle, Box, Button, CircularProgress, Paper, Stack, Typography} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleCheck, faClockRotateLeft, faCircleQuestion} from "@fortawesome/pro-solid-svg-icons";
import {format, parseISO} from "date-fns";
import {AxiosError} from "axios";
import {useTranslation} from "react-i18next";
import {useApplicationContext} from "../../ApplicationContext";
import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
// Der Acknowledge-Endpunkt liefert seine Antwort untypisiert (IActionResult ohne
// ProducesResponseType), daher generiert der Swagger-Client keinen Typ. Die Laufzeitform ist
// stabil und hier lokal nachgebildet.
interface RegistrationAcknowledgeResult {
    status: "NotFound" | "Acknowledged" | "AlreadyAcknowledged" | string;
    acknowledgedAt: string | null;
    customer: string | null;
    acknowledgedByEmail: string | null;
}

type ViewState =
    | { kind: "loading" }
    | { kind: "result"; data: RegistrationAcknowledgeResult }
    | { kind: "error"; message: string };

/**
 * Bestätigungsseite für den Vertriebs-Link aus der Registrierungs-Mail.
 * Beim Mount wird einmalig POST /User/Registration/FollowUps/{token}/Acknowledge
 * aufgerufen. Aus der Antwort werden drei Zustände gerendert: "neu bestätigt",
 * "war bereits bestätigt" und "Token unbekannt".
 */
export const RegistrationAcknowledge: React.FC = () => {
    useDocumentTitle("registrationAcknowledge.title");
    const {token} = useParams();
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [state, setState] = useState<ViewState>({kind: "loading"});
    const calledRef = useRef(false);

    useEffect(() => {
        if (calledRef.current) return;
        if (!token) {
            setState({kind: "error", message: t("registrationAcknowledge.errors.missingToken")});
            return;
        }
        calledRef.current = true;

        context.api.user
            .userAcknowledgeRegistration(token)
            .then(response => setState({kind: "result", data: response.data}))
            .catch((err: unknown) => {
                if (err instanceof AxiosError) {
                    const data = err.response?.data as RegistrationAcknowledgeResult | undefined;
                    if (data && data.status === "NotFound") {
                        setState({kind: "result", data});
                        return;
                    }
                }
                setState({kind: "error", message: t("registrationAcknowledge.errors.unexpected")});
            });
    }, [token, context.api, t]);

    const formatTimestamp = (iso: string | null): string =>
        iso ? format(parseISO(iso), "dd.MM.yyyy HH:mm") : "";

    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%"}}>
            <Paper elevation={2} sx={{p: 4, maxWidth: 640, width: "100%", mt: 4}}>
                {state.kind === "loading" && (
                    <Stack spacing={2} sx={{alignItems: "center"}}>
                        <CircularProgress/>
                        <Typography variant="body1">
                            {t("registrationAcknowledge.loading")}
                        </Typography>
                    </Stack>
                )}

                {state.kind === "error" && (
                    <Alert severity="error">
                        <AlertTitle>{t("registrationAcknowledge.errors.title")}</AlertTitle>
                        {state.message}
                    </Alert>
                )}

                {state.kind === "result" && state.data.status === "Acknowledged" && (
                    <Stack spacing={2} sx={{alignItems: "center"}}>
                        <FontAwesomeIcon icon={faCircleCheck} size="3x" color="#2e7d32"/>
                        <Typography variant="h5" color="success.main">
                            {t("registrationAcknowledge.acknowledged.title")}
                        </Typography>
                        <Typography variant="body1" align="center">
                            {state.data.customer
                                ? t("registrationAcknowledge.acknowledged.bodyWithCustomer", {customer: state.data.customer})
                                : t("registrationAcknowledge.acknowledged.body")}
                        </Typography>
                        {state.data.acknowledgedAt && (
                            <Typography variant="body2" color="text.secondary">
                                {t("registrationAcknowledge.acknowledged.at", {at: formatTimestamp(state.data.acknowledgedAt)})}
                            </Typography>
                        )}
                    </Stack>
                )}

                {state.kind === "result" && state.data.status === "AlreadyAcknowledged" && (
                    <Stack spacing={2} sx={{alignItems: "center"}}>
                        <FontAwesomeIcon icon={faClockRotateLeft} size="3x" color="#0288d1"/>
                        <Typography variant="h5" color="info.main">
                            {t("registrationAcknowledge.alreadyAcknowledged.title")}
                        </Typography>
                        <Typography variant="body1" align="center">
                            {state.data.customer
                                ? t("registrationAcknowledge.alreadyAcknowledged.bodyWithCustomer", {customer: state.data.customer})
                                : t("registrationAcknowledge.alreadyAcknowledged.body")}
                        </Typography>
                        {state.data.acknowledgedByEmail && state.data.acknowledgedAt && (
                            <Typography variant="body2" color="text.secondary" align="center">
                                {t("registrationAcknowledge.alreadyAcknowledged.byAt", {
                                    by: state.data.acknowledgedByEmail,
                                    at: formatTimestamp(state.data.acknowledgedAt),
                                })}
                            </Typography>
                        )}
                    </Stack>
                )}

                {state.kind === "result" && state.data.status === "NotFound" && (
                    <Stack spacing={2} sx={{alignItems: "center"}}>
                        <FontAwesomeIcon icon={faCircleQuestion} size="3x" color="#ed6c02"/>
                        <Typography variant="h5" color="warning.main">
                            {t("registrationAcknowledge.notFound.title")}
                        </Typography>
                        <Typography variant="body1" align="center">
                            {t("registrationAcknowledge.notFound.body")}
                        </Typography>
                    </Stack>
                )}

                <Stack direction="row" sx={{justifyContent: "center", mt: 4}}>
                    <Button variant="contained" onClick={() => navigate("/admin/registrations")}>
                        {t("registrationAcknowledge.toOverview")}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export default RegistrationAcknowledge;
